import { Box, Button, Grid, Group, Modal, Text } from '@mantine/core';
import { IconPencil, IconPlus } from '@tabler/icons-react';
import queryString from 'query-string';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import SearchableCombobox from '../SearchableCombobox';
import Spinner from '../Spinner';
import SegmentCounter from './SegmentCounter';
import SegmentDetailsForm from './SegmentDetailsForm';
import SegmentNewForm from './SegmentNewForm';

const SegmentSelector = ({
  segmentId,
  onSelect,
  campaignScope,
  scope,
  title,
  description,
  dataTour,
  sizeTitle = 4,
  sizeBody = 8,
  disabled,
  optional,
  idMapper = (segment) => segment.id,
  loadedSegments = [],
  disabledScopes = [],
}) => {
  const { domain } = useSelector((state) => state.domain);
  const googleAccount = useSelector((state) => state.google.account);

  const fetch = useFetch();
  const [segmentForModal, setSegmentForModal] = useState(false);
  const [error, setError] = useState();
  const [segments, setSegments] = useState(loadedSegments);
  const [dirty, setDirty] = useState(false);

  const segment = segments?.find((s) => idMapper(s)?.toString() === segmentId?.toString());

  const loadSegments = useCallback(() => {
    const where = {
      scope: [scope],
      sortByColumn: 'name',
      order: 'ASC',
    };

    if (scope === 'google') {
      where.hasExternalId = [true];
    }

    const params = queryString.stringify(where, { arrayFormat: 'bracket' });

    return fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/segments?${params}`)
      .then(({ segments }) => {
        if (scope === 'google') {
          const filteredSegments = segments.filter((segment) =>
            segment.externalId.includes(googleAccount?.adAccountId)
          );
          setSegments(filteredSegments);
        } else {
          setSegments(segments);
        }
      })
      .catch((error) => {
        setError(error);
      });
  }, [domain.accountId, domain.id, fetch, googleAccount?.adAccountId, scope]);

  const loadedSegmentsStringified = JSON.stringify(loadedSegments);
  useEffect(() => {
    if (loadedSegments.length) {
      setSegments(JSON.parse(loadedSegmentsStringified));
    } else {
      loadSegments();
    }
  }, [loadSegments, loadedSegments.length, loadedSegmentsStringified]);

  if (!segments) {
    return <Spinner />;
  }

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  const options = optional ? [{ value: '', label: '(None)' }] : [];

  options.push(...segments.map((segment) => ({ value: idMapper(segment)?.toString(), label: segment.name })));

  return (
    <>
      <Modal
        opened={!!segmentForModal}
        onClose={() => setSegmentForModal(undefined)}
        size="full"
        styles={{ content: { width: '100%', maxWidth: '100%' } }}
        title={`${segmentForModal?.id ? 'Edit' : 'Create'} Segment`}
      >
        {segmentForModal && (
          <>
            {segmentForModal.id ? (
              <SegmentDetailsForm
                segment={segmentForModal}
                onSave={() => {
                  loadSegments().then(() => {
                    setSegmentForModal(undefined);
                    onSelect(segmentForModal);
                  });
                }}
                onBack={() => setSegmentForModal(undefined)}
                disabledScopes={disabledScopes}
              />
            ) : (
              <SegmentNewForm
                onSave={(segment) => {
                  loadSegments().then(() => {
                    setSegmentForModal(undefined);
                    onSelect(segment);
                  });
                }}
                onBack={() => setSegmentForModal(undefined)}
                disabledScopes={disabledScopes}
              />
            )}
          </>
        )}
      </Modal>

      <Grid data-tour={dataTour} mt="md">
        {title && (
          <Grid.Col span={{ base: 12, sm: sizeTitle }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>
              {title}
              {!optional && '*'}
            </label>
          </Grid.Col>
        )}

        <Grid.Col span={{ base: 12, sm: title ? sizeBody : 12 }}>
          <Grid align="center">
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Group gap="xs" wrap="nowrap">
                <div style={{ flex: 1 }}>
                  <SearchableCombobox
                    options={options}
                    value={segmentId?.toString()}
                    onChange={(value) => {
                      setDirty(true);
                      onSelect(segments?.find((s) => idMapper(s)?.toString() === value?.toString()));
                    }}
                    placeholder="(Select segment)"
                    searchPlaceholder="Search segments..."
                    emptyMessage="No segments found"
                    disabled={disabled}
                    error={dirty && !segmentId && !optional}
                    onBlur={() => setDirty(true)}
                  />
                </div>
                <Button disabled={disabled} variant="default" onClick={() => setSegmentForModal({})}>
                  <IconPlus size={16} />
                </Button>
                {segment && (
                  <Button disabled={disabled} variant="default" onClick={() => setSegmentForModal(segment)}>
                    <IconPencil size={16} />
                  </Button>
                )}
              </Group>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <SegmentCounter size="sm" segment={segment} scope={campaignScope} showSample={false} />
            </Grid.Col>
          </Grid>
          <Box mt="xs">{description}</Box>
          {!optional && dirty && !segmentId && (
            <Text size="xs" c="red">
              Segment is required
            </Text>
          )}
        </Grid.Col>
      </Grid>
    </>
  );
};

export default SegmentSelector;
